import { useEffect, useRef, useState } from "react";
import { loadLeaflet } from "./loadLeaflet";

interface LeafletMapProps {
  lat: number;
  lon: number;
  onPositionChange: (lat: number, lon: number) => void;
}

interface LLatLng {
  lat: number;
  lng: number;
}

interface LMarker {
  getLatLng(): LLatLng;
  setLatLng(latlng: [number, number]): LMarker;
  on(event: string, handler: () => void): LMarker;
  addTo(map: LMap): LMarker;
}

interface LMap {
  setView(latlng: [number, number], zoom: number): LMap;
  invalidateSize(): LMap;
  remove(): void;
}

interface LTileLayer {
  addTo(map: LMap): LTileLayer;
}

interface LeafletLib {
  map(container: HTMLElement, options: Record<string, unknown>): LMap;
  tileLayer(url: string, options: Record<string, unknown>): LTileLayer;
  marker(latlng: [number, number], options?: Record<string, unknown>): LMarker;
}

const STREET_ZOOM = 17;
const TILE_URL =
  "https://tile2.maps.2gis.com/tiles?x={x}&y={y}&z={z}&v=1&r=g&ts=online_sd";

const LeafletMap: React.FC<LeafletMapProps> = ({
  lat,
  lon,
  onPositionChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onPositionChange);
  callbackRef.current = onPositionChange;
  const [loadError, setLoadError] = useState(false);

  // Always up-to-date — read in async init after Leaflet loads
  const latRef = useRef(lat);
  const lonRef = useRef(lon);
  latRef.current = lat;
  lonRef.current = lon;

  // Becomes a real function once Leaflet is ready, null while loading
  const moveMapRef = useRef<((lat: number, lon: number) => void) | null>(null);

  // ── Init map once ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;
    let mapInstance: LMap | null = null;

    loadLeaflet()
      .then((lib) => {
        const L = lib as LeafletLib;
        if (cancelled || !containerRef.current) return;

        // Read the LATEST coords — user may have already picked an address while Leaflet loaded
        const initLat = latRef.current;
        const initLon = lonRef.current;

        const map = L.map(containerRef.current, {
          center: [initLat, initLon],
          zoom: STREET_ZOOM,
          maxZoom: 18,
          attributionControl: false,
        });
        mapInstance = map;

        L.tileLayer(TILE_URL, {
          maxZoom: 18,
          maxNativeZoom: 18,
          tms: false,
          attribution:
            '&copy; <a href="https://2gis.ru" target="_blank" rel="noopener noreferrer">2GIS</a>',
        }).addTo(map);

        const marker = L.marker([initLat, initLon], { draggable: true }).addTo(
          map,
        );

        let dragging = false;
        marker.on("dragstart", () => {
          dragging = true;
        });
        marker.on("dragend", () => {
          dragging = false;
          const pos = marker.getLatLng();
          callbackRef.current(pos.lat, pos.lng);
        });

        // Expose move function — only callable once map is ready
        moveMapRef.current = (newLat: number, newLon: number) => {
          if (dragging) return;
          const cur = marker.getLatLng();
          if (
            Math.abs(cur.lat - newLat) < 0.000001 &&
            Math.abs(cur.lng - newLon) < 0.000001
          ) {
            return;
          }
          marker.setLatLng([newLat, newLon]);
          map.setView([newLat, newLon], STREET_ZOOM);
        };

        setTimeout(() => map.invalidateSize(), 300);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });

    return () => {
      cancelled = true;
      moveMapRef.current = null;
      if (mapInstance) {
        mapInstance.remove();
        mapInstance = null;
      }
    };
  }, []);

  // ── Move map when coords change ──────────────────────────────────────────────
  // If moveMapRef is null (Leaflet still loading) — latRef/lonRef handle it in init above.
  useEffect(() => {
    moveMapRef.current?.(lat, lon);
  }, [lat, lon]);

  if (loadError) {
    return (
      <div className="sc-drawer-map sc-drawer-map--error">
        Не удалось загрузить карту
      </div>
    );
  }

  return <div ref={containerRef} className="sc-drawer-map" />;
};

export default LeafletMap;
